import { createStyles, Box, useMantineTheme, Input, Chip, Group, MultiSelect, Button, Text, Avatar, CloseButton, Popover, ScrollArea } from '@mantine/core';
import moment from 'moment';
import { forwardRef, useCallback, useEffect, useState } from 'react';

import './CustomCalendar.scss';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { logger } from 'utils/logger';
import EventSummary from './EventSummary';
import { closeModal, openModal } from '@mantine/modals';
import { selectSeason } from 'modules/Seasons/seasonSlice';
import { useDispatch } from 'react-redux';
import { selectSession, selectTask } from 'modules/Sessions/sessionSlice';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconX } from '@tabler/icons-react';
import { FormInputWrapper, FilterButton, ChannelSelectItem, ChannelValue, ChannelBadge } from 'core/components/index';
import { useForm } from '@mantine/form';
import { getUserAvatarColour, getUserFullname, getUserInitials, matchesKeywords } from 'utils/helper';
import _ from 'lodash';
import { SessionStatusTypes } from 'core/models/SampleModel';
import { SETTINGS, shotChannels } from 'utils/constants';

const normalizeChannels = (item) => {
    if (item.channels?.length) {
        return Array.isArray(item.channels) ? item.channels : item.channels.split(',');
    }
    if (item.channel?.length) {
        return Array.isArray(item.channel) ? item.channel : [item.channel];
    }
    return [];
};

const EVENT_BG_OPACITY = { dark: 0.38, light: 0.8 };
const EVENT_BORDER_OPACITY = { dark: 0.7, light: 0.9 };

const getEventRgba = (theme, colorName, fallback = 'gray') => {
    const palette = theme.colors[colorName] ?? theme.colors[fallback];
    const base = theme.colorScheme === 'dark' ? palette[6] : palette[7];
    const scheme = theme.colorScheme === 'dark' ? 'dark' : 'light';
    return {
        background: theme.fn.rgba(base, EVENT_BG_OPACITY[scheme]),
        border: theme.fn.rgba(base, EVENT_BORDER_OPACITY[scheme]),
    };
};

const getEventColourFromChannels = (channels, theme, fallback = 'gray') => {
    const channel = shotChannels.find((c) => c.value === channels[0]);
    const colorName = channel?.color ?? fallback;
    return getEventRgba(theme, colorName, fallback);
};

const useStyles = createStyles((theme) => ({
    root: {
        //   padding: theme.spacing.xl * 1.5,
        // paddingTop:theme.spacing.xl * 1,
    },

    container: {
        // height: '100%',
        '.rbc-calendar': {
            fontSize: 14,
            height: '100%',
            '.rbc-month-view,.rbc-time-view': {
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
            },
        },
        '.rbc-day-bg + .rbc-day-bg,.rbc-time-content,.rbc-time-content > * + * > *,.rbc-day-slot .rbc-time-slot,.rbc-timeslot-group': {
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
        },
        '.rbc-month-row + .rbc-month-row': {
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
        },
        '.rbc-month-row': {
            minHeight: 150,
        },
        '.rbc-today': {
            backgroundColor: theme.colorScheme === 'dark' ? `${theme.colors.dark[5]} !important` : theme.colors.gray[3],
        },
        '.rbc-day-bg': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
        },
        '.rbc-off-range-bg': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1],
        },
        '.rbc-header,.rbc-header + .rbc-header,.rbc-time-header-content, .rbc-time-header.rbc-overflowing': {
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
        },
        '.rbc-toolbar button': {
            fontWeight: 600,
            fontSize: 14,
            height: 36,
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
            color: theme.colorScheme === 'dark' ? 'white' : 'inherit',
            cursor: 'pointer',
        },
        '.rbc-toolbar button:hover,.rbc-toolbar button:focus': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.brand[7] : theme.colors.brand[6],
            color: theme.white,
            borderColor: 'transparent',
        },
        '.rbc-toolbar button:active, .rbc-toolbar button.rbc-active': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.brand[4] : theme.colors.brand[6],
            color: theme.white,
            borderColor: 'transparent',
        },
        '.rbc-toolbar button:active:hover, .rbc-toolbar button:active:focus, .rbc-toolbar button.rbc-active:hover, .rbc-toolbar button.rbc-active:focus': {
            backgroundColor: theme.colors.brand[4],
            color: theme.white,
            borderColor: 'transparent',
        },
        '.rbc-label': {
            fontSize: 14,
        },
        '.rbc-show-more': {
            background: 'none',
            color: theme.colors.brand[4],
            borderRadius: 4,
            padding: '2px 3px',
            ':hover': {
                background: theme.colors.brand[7],
                color: theme.white,
            },
        },
        '.rbc-agenda-view table.rbc-agenda-table thead > tr > th,.rbc-agenda-view table.rbc-agenda-table,.rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td': {
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
        },
        '.rbc-agenda-view table': {
            color: theme.white,
        },
        '.rbc-time-view': {
            '.rbc-events-container > div': {
                position: 'relative',
                width: '100% !important',
                left: '0 !important',
                height: 'auto !important',
                paddingBottom: 8,
                '.rbc-event-label': {
                    display: 'none',
                },
                '.mantine-Text-root': {
                    whiteSpace: 'pre-wrap',
                    marginBottom: 20,
                },
            },
            '.rbc-time-slot': {
                border: 0,
            },
            '.rbc-time-slot span': {
                visibility: 'hidden',
            },
            '.rbc-day-slot .rbc-time-slot': {
                borderColor: 'transparent',
            },
            '.rbc-timeslot-group': {
                border: 0,
                // borderBottomColor:'transparent',
            },
        },
        '.rbc-month-view': {
            '.event-title': {
                maxWidth: 200,
            },
        },
        '.rbc-event': {
            transition: 'filter 0.15s ease, transform 0.15s ease',
            '&:hover': {
                filter: 'brightness(1.15)',
                transform: 'translateY(0px)',
            },
        },
    },
    label: {
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        textTransform: 'uppercase',
        fontSize: 12,
        fontWeight: 700,
    },
    calendar: {
        height: '100%',
    },
}));

function Event({ event }) {
    return (
        <a href={event.route} onClick={(e) => e.preventDefault()} style={{ display: 'block', textDecoration: 'none', color: 'inherit', height: 'auto' }}>
            <div style={{ height: 'auto', display: 'flex', flexDirection: 'column', gap: 3, padding: '2px 0' }}>
                <Text size={10} weight={700} transform='uppercase' style={{ opacity: 0.75, letterSpacing: 0.4 }} truncate>
                    {event.typeLabel}
                    {event.code ? ` · ${event.code}` : ''}
                </Text>
                <Text className='event-title' size='sm' weight={600} lineClamp={2} style={{ lineHeight: 1.25 }}>
                    {event.name ?? event.title}
                </Text>
                <Group position='apart' noWrap spacing={4} mt={2}>
                    <Group spacing={0} style={{ flexShrink: 0 }}>
                        {event.team?.map((member, idx) => {
                            const userInitials = member.name ? getUserInitials(member.name.split(' ')[0], member.name.split(' ')[1]) : '';
                            const themeCol = getUserAvatarColour(userInitials);
                            return (
                                <Avatar key={idx} variant='filled' sx={{ marginLeft: idx ? -10 : 0 }} title={member.name} color={themeCol} size={25} radius={40}>
                                    {/* {member.name?.charAt(0)} */}
                                    {userInitials}
                                </Avatar>
                            );
                        })}
                    </Group>
                    {event.channels?.length > 0 && <ChannelBadge channels={[event.channels[0]]} size='xs' sx={{ flexShrink: 1, minWidth: 0 }} />}
                </Group>
            </div>
        </a>
    );
}

function EventAgenda({ event }) {
    return (
        <span>
            <em style={{ color: 'white' }}>{event.title}</em>
            <p>{event.desc}</p>
        </span>
    );
}

const localizer = momentLocalizer(moment);

const EventFilter = { types: ['session'], keyword: '', members: [], channels: [], brands: [], categories: [], department: [], location: [], status: [], colour: '' };

const MemberItem = forwardRef(({ name, role, color, labelClass, ...others }, ref) => {
    const userInitials = getUserInitials(name.split(' ')[0], name.split(' ')[1]);
    return (
        <div ref={ref} {...others}>
            <Group noWrap>
                <Avatar variant='filled' title={`${name} - ${role}`} color={color} size={30} radius={40}>
                    {/* {name.charAt(0)} */}
                    {userInitials}
                </Avatar>
                <Text sx={{ paddingLeft: 0 }} className={labelClass}>
                    {name}
                </Text>
            </Group>
        </div>
    );
});

function MemberValue({ label, onRemove }) {
    return (
        <div>
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    cursor: 'default',
                    alignItems: 'center',
                    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[4]}`,
                    paddingLeft: theme.spacing.xs,
                    borderRadius: theme.radius.sm,
                })}>
                {/* <Box mr={10}>
                    <Avatar variant='filled' title={`${name} - ${team}`} color={color} size={10} radius={40}>
                       {userInitials}
                    </Avatar>
                </Box> */}
                <Box sx={{ lineHeight: 1, fontSize: 12 }}>{label}</Box>
                <CloseButton onMouseDown={onRemove} variant='transparent' size={12} iconSize={14} tabIndex={-1} />
            </Box>
        </div>
    );
}

const AltCalendar = ({ sessions = [], seasons = [], tasks = [], aggData, user, guides = [] }) => {
    const { classes } = useStyles();
    const theme = useMantineTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [filterCount, setFilterCount] = useState(0);
    const [filterOpen, setFilterOpen] = useState(false);
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [members, setMembers] = useState([]);

    const [events, setEvents] = useState([
        {
            start: moment().toDate(),
            end: moment().add(1, 'days').toDate(),
            title: 'Some title',
            type: '',
        },
    ]);

    const [filteredEvents, setFilteredEvents] = useState([]);

    const [eventFilter, setEventFilter] = useState(EventFilter);
    const [, setFilterItems] = useState(eventFilter);

    useEffect(() => {
        if (aggData && aggData.productionTeam) {
            const productionTeam = aggData.productionTeam.map((val) => {
                const userInitials = val.name ? getUserInitials(val.name.split(' ')[0], val.name.split(' ')[1]) : '';
                const themeCol = getUserAvatarColour(userInitials);
                return { id: val.id, name: val.name, email: val.email, color: themeCol, team: 'Production', initials: userInitials };
            });
            const postTeam = aggData.postProductionTeam.map((val) => {
                const userInitials = val.name ? getUserInitials(val.name.split(' ')[0], val.name.split(' ')[1]) : '';
                const themeCol = getUserAvatarColour(userInitials);
                return { id: val.id, name: val.name, email: val.email, color: themeCol, team: 'Post Production', initials: userInitials };
            });
            var tmpMembers = postTeam.concat(productionTeam);
            tmpMembers.sort((a, b) => a.name.localeCompare(b.name));

            const newMembers = tmpMembers.map((val, idx) => {
                return { ...val, value: idx, label: val.name, group: val.team };
            });
            setMembers([...newMembers]);
        }
    }, [aggData]);

    const filterData = useCallback(() => {
        logger.log(events);
        const data = events.filter((item) => {
            var match = true;

            if (eventFilter.types && eventFilter.types.length) {
                match = eventFilter.types.indexOf(item.type) > -1 ? true : false;
            }

            if (eventFilter.members && eventFilter.members.length && match) {
                // see if the team members exist in the event.
                var tmpMatch = false;
                for (var i = 0; i < eventFilter.members.length; i++) {
                    const val = eventFilter.members[i];
                    let member = members[val];
                    if (val.email === user.email) {
                        member = user;
                    }

                    logger.log(item);
                    const isFound = item.team.find((val) => val.email === member.email);

                    if (isFound) {
                        tmpMatch = true;
                        break;
                    }
                }

                match = tmpMatch;
            }

            // filter by brand
            // if (eventFilter.brands && eventFilter.brands.length) {
            //     match = eventFilter.brands.indexOf(item.facet_brand) > -1 ? true : false;
            // }

            // if (eventFilter.categories && eventFilter.categories.length && match) {
            //     match = eventFilter.categories.indexOf(item.metadata_toplevelcategory) > -1 ? true : false;
            // }

            if (eventFilter.keyword && eventFilter.keyword.length && match) {
                match = matchesKeywords(eventFilter.keyword, item.title, item.type, item.data.tags?.join(' '), item.uuid);
            }

            if (eventFilter.channels && eventFilter.channels.length && match) {
                const itemChannels = normalizeChannels(item.data);
                match = itemChannels.some((ch) => eventFilter.channels.includes(ch));
            }

            return match;
        });

        setFilteredEvents(data);
    }, [events, eventFilter.types, eventFilter.members, eventFilter.channels, eventFilter.brands, eventFilter.categories, eventFilter.keyword, members, user?.email]);

    const setFilterVars = (vals) => {
        logger.log(vals, ' FILTER VALS');
        setEventFilter(vals);
    };

    useEffect(() => {
        logger.log('setting samples, ');
        //setSampleData(samples);

        // set the brands/season/category
        var members = {};
        var types = {};
        var brands = {};
        var categories = {};
        var seasons = {};

        events.forEach((item, idx) => {
            members[item.id] = item;
            types[item.type] = item;
            brands[item.facet_brand] = item;
            categories[item.metadata_toplevelcategory] = item;
            seasons[item.facet_season] = item;
        });

        setFilterItems({ members, types, brands, categories, seasons });

        filterData();
    }, [events, filterData]);

    useEffect(() => {
        // filter the data.
        filterData();
    }, [eventFilter, filterData]);

    const getFilterCount = (val) => {
        let count = 0;
        if (val.channels?.length) count++;
        if (val.types?.length) count++;
        if (val.members?.length) count++;
        return count;
    };

    useEffect(() => {
        const count = getFilterCount(eventFilter);
        setFilterCount(count);
    }, [eventFilter]);

    const onClearClick = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();

        setEventFilter({...EventFilter,types:[]});
        setAssignedToMe(false);
        logger.log('CLEAR CLICK', e);
    };

    const filterButtonHandler = (e) => {
        logger.log('BUTTON CLICK', e, e.nativeEvent);
        setFilterOpen((o) => !o);
    };

    const onChipChange = (val) => {
        const tmpUser = getUserFullname(user.first, user.last);
        var filtervars = { ...eventFilter };
        if (val) {
            setAssignedToMe(true);
            // push the current user into the filter params
            filtervars.members.push({ name: tmpUser, email: user.email, team: '', label: tmpUser, value: members.length });
        } else {
            setAssignedToMe(false);
            _.remove(filtervars.members, (member) => member.email === user.email);
        }
        setEventFilter(filtervars);
    };

    useEffect(() => {
        if (sessions) {
            var mergedArray = sessions
                .filter((val) => {
                    return val.status !== SessionStatusTypes.ARCHIVE;
                })
                .map((val) => ({ ...val, type: 'session' }))
                .concat(seasons.map((val) => ({ ...val, type: 'season' })));

            mergedArray = mergedArray.concat(tasks.map((val) => ({ ...val, type: 'task' })));

            const tmpEvents = mergedArray.map((val) => {
                var title, route, eventColour, eventBorderColour, startDate, endDate, team, uuid, typeLabel, code, name;
                switch (val.type) {
                    case 'task': {
                        typeLabel = 'Task';
                        code = val.taskPart;
                        name = val.description;
                        title = `Task: ${val.taskPart}`;
                        route = `/sessions/tasks/${val.taskSort}`;
                        const taskColours = getEventColourFromChannels(normalizeChannels(val), theme, 'pink');
                        eventColour = taskColours.background;
                        eventBorderColour = taskColours.border;
                        startDate = moment(val.deadline).toDate();
                        endDate = moment(val.deadline).toDate();
                        team = [{ name: val.assignee, email: val.assigneeEmail, id: val.assigneeEmail }];
                        uuid = val.taskPart;
                        break;
                    }
                    case 'season': {
                        typeLabel = 'Event';
                        code = '';
                        name = val.name;
                        title = `Event: ${val.name}`;
                        route = `/${val.type}s/${val.seasonSort}`;
                        const seasonColours = getEventColourFromChannels(normalizeChannels(val), theme, 'blue');
                        eventColour = seasonColours.background;
                        eventBorderColour = seasonColours.border;
                        startDate = moment(val.deadline).toDate();
                        endDate = moment(val.deadline).toDate();
                        uuid = val.id;
                        team = [];
                        break;
                    }
                    case 'session': {
                        typeLabel = 'Session';
                        code = val.code;
                        name = val.name;
                        title = `Session: ${val.code} - ${val.name}`;
                        route = `/${val.type}s/${val.sessionId}`;
                        const sessionColours = getEventColourFromChannels(normalizeChannels(val), theme, 'grape');
                        eventColour = sessionColours.background;
                        eventBorderColour = sessionColours.border;
                        startDate = moment(val.shootDate).toDate();
                        endDate = moment(val.shootDate).toDate();
                        team = val.team;
                        uuid = val.id;
                        break;
                    }
                    default:
                        break;
                }

                startDate.setHours(0, 0);
                endDate.setHours(2.5, 0);
                return {
                    start: startDate,
                    end: endDate,
                    title: title,
                    typeLabel: typeLabel,
                    code: code,
                    name: name,
                    colour: eventColour,
                    borderColour: eventBorderColour,
                    id: val.id,
                    data: val,
                    type: val.type,
                    route: route,
                    team: team,
                    uuid: uuid,
                    channels: normalizeChannels(val),
                };
            });

            setEvents(tmpEvents);
            setFilteredEvents([...tmpEvents]);
        }
    }, [sessions, tasks, seasons, theme]);

    const eventStyleGetter = (event) => {
        return {
            style: {
                opacity: 1,
                borderColor: event.borderColour ?? event.colour,
                backgroundColor: event.colour,
                borderWidth: 1,
                borderStyle: 'solid',
            },
        };
    };

    const onEventSelect = (data) => {
        switch (data.type) {
            case 'season': {
                dispatch(selectSeason(data.data));
                break;
            }
            case 'session': {
                var tmpGuides = data.data.guides;
                if (tmpGuides) {
                    tmpGuides = guides.filter((guide) => {
                        var guideIds = data.data.guides.map((g) => g.id);
                        return guideIds.indexOf(guide.id) > -1;
                    });
                }
                dispatch(selectSession({ ...data.data, guides: tmpGuides }));
                break;
            }
            case 'task': {
                dispatch(selectTask(data.data));
                break;
            }
            default: {
                break;
            }
        }

        const onViewClick = () => {
            closeModal('eventModal');
            navigate(data.route);
        };

        openModal({
            title: `${data.title} overview`,
            size: 'lg',
            modalId: 'eventModal',
            children: (
                <>
                    <EventSummary data={data} onViewClick={onViewClick} seasons={seasons} />
                </>
            ),
        });
    };

    const components = {
        agenda: {
            event: EventAgenda,
        },
        event: Event,
    };

    return (
        <Box className={classes.container}>
            <Group
                align='start'
                className='container-action-bar'
                sx={{ width: '100%', position: 'sticky', top: 50, background: theme.colorScheme === 'dark' ? theme.colors.dark[8] : 'white', zIndex: 9 }}
                position='apart'
                height={'100%'}
                pt='xl'
                pb={'sm'}
                mb={'sm'}>
                <Group>
                    <Input
                        className='tab-filter-search'
                        icon={<IconSearch />}
                        placeholder='Search Events'
                        value={eventFilter.keyword}
                        onChange={(e) =>
                            setEventFilter({
                                ...eventFilter,
                                keyword: e.currentTarget.value,
                            })
                        }
                        rightSection={
                            eventFilter.keyword?.length ? (
                                <IconX
                                    size={14}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setEventFilter({ ...eventFilter, keyword: '' })}
                                />
                            ) : null
                        }
                    />

                    <Chip size='lg' className='taskbar-chip' checked={assignedToMe} onChange={(val) => onChipChange(val)} sx={{ label: { fontSize: 14 } }}>
                        Assigned to me
                    </Chip>
                </Group>

                <Group align='start' position='right'>
                    <Popover shadow='sm' opened={filterOpen} onClose={() => setFilterOpen(false)} width={600} position='bottom-end' withArrow closeOnClickOutside={true}>
                        <Popover.Target>
                            <div>
                                <FilterButton onClick={filterButtonHandler} onClear={onClearClick} count={filterCount} />
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown className='filter-panel-dropdown-wrapper'>
                            <div style={{ display: 'flex' }}>
                                <FilterPanel
                                    filter={eventFilter}
                                    members={members}
                                    theme={theme}
                                    onCancel={() => setFilterOpen(false)}
                                    onApply={(vals) => {
                                        setFilterVars(vals);
                                        setFilterOpen(false);
                                    }}
                                />
                            </div>
                        </Popover.Dropdown>
                    </Popover>
                </Group>
            </Group>

            <Calendar
                components={components}
                showMultiDayTimes={true}
                onSelectEvent={onEventSelect}
                localizer={localizer}
                defaultDate={new Date()}
                defaultView='week'
                events={filteredEvents}
                eventPropGetter={eventStyleGetter}
            />
        </Box>
    );
};

export default AltCalendar;

const FilterPanel = ({ onCancel, onApply, filter, members, theme }) => {
    const form = useForm({
        initialValues: {
            ...filter,
        },
    });

    const channelOptions = shotChannels.map((channel) => ({ ...channel }));

    const eventTypeOptions = [
        { label: 'Events', value: 'season', color: theme.colors.blue[6] },
        { label: 'Sessions', value: 'session', color: theme.colors.grape[6] },
        { label: 'Tasks', value: 'task', color: theme.colors.pink[6] },
    ];

    return (
        <Box shadow='xs' style={{ width: '100%' }}>
            <form onSubmit={form.onSubmit((values) => onApply(values))}>
                <ScrollArea.Autosize mah={SETTINGS.FILTER_PANEL_MAX_HEIGHT_LG} className='filter-scroll-content'>
                    <FormInputWrapper label='Channels' id='input-channels' labelWidth='120px'>
                        <MultiSelect
                            data={channelOptions}
                            withinPortal={true}
                            label=''
                            placeholder='Select channels'
                            searchable
                            nothingFound='Nothing found'
                            itemComponent={ChannelSelectItem}
                            valueComponent={ChannelValue}
                            {...form.getInputProps('channels')}
                        />
                    </FormInputWrapper>

                    <FormInputWrapper label='Event Types' id='input-event-types' labelWidth='120px'>
                        <MultiSelect
                            withinPortal={true}
                            data={eventTypeOptions}
                            label=''
                            placeholder='Select event types'
                            searchable
                            nothingFound='Nothing found'
                            // itemComponent={IndicatorSelectItem}
                            {...form.getInputProps('types')}
                        />
                    </FormInputWrapper>

                    <FormInputWrapper label='Users' id='input-users' labelWidth='120px'>
                        <MultiSelect
                            withinPortal={true}
                            data={members}
                            label=''
                            placeholder='Select users'
                            searchable
                            nothingFound='Nothing found'
                            // valueComponent={MemberValue}
                            itemComponent={MemberItem}
                            {...form.getInputProps('members')}
                        />
                    </FormInputWrapper>
                </ScrollArea.Autosize>

                <Group position='apart' mt='md' className='action-bar'>
                    <Button variant='default' size='sm' onClick={() => onCancel()}>
                        Cancel
                    </Button>
                    <Button type='submit' size='sm'>
                        Apply
                    </Button>
                </Group>
            </form>
        </Box>
    );
};
